import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { isEntraConfigured, loginRequest } from '../auth/msalConfig';
import { getImpersonationEmail } from '../auth/impersonation';
import { stashDeepLink } from '../auth/deepLinkStash';

const BASE_URL = import.meta.env.VITE_API_URL || '';
// API_KEY is for LOCAL DEV ONLY (Entra not configured). The production
// build does NOT inject VITE_API_KEY (see deploy.yml) — anything below
// would only ship `'dev-key'`, which the prod backend rejects. The
// `else` branch that uses this falls through to dead code in prod
// because `isEntraConfigured` is always true there.
const API_KEY = import.meta.env.VITE_API_KEY || 'dev-key';

// Gate to prevent multiple concurrent redirect attempts
let redirecting = false;

/**
 * Attaches the Entra bearer token, starting an interactive redirect when it can't be refreshed
 * silently. Throws whenever the request must not proceed.
 *
 * ⚠ **Extracted so the deep-link stash happens once.** `apiFetch` / `apiFetchText` /
 * `apiFetchBlob` carried three byte-identical copies of this block, four redirect sites each — and
 * none of the twelve stashed the current URL, so any re-auth triggered from an API call returned
 * the user to `/` instead of the record they were reading (see
 * [`auth/deepLinkStash`](../auth/deepLinkStash.ts)). Twelve copies of an invariant is twelve
 * chances to forget it; one is one.
 */
async function attachAuth(headers: Record<string, string>): Promise<void> {
  const { msalInstance } = await import('../main');
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    // No accounts — trigger login (only once)
    if (!redirecting) {
      redirecting = true;
      stashDeepLink();
      await msalInstance.loginRedirect(loginRequest);
    }
    throw new Error('Not authenticated');
  }
  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    headers['Authorization'] = `Bearer ${tokenResponse.idToken}`;
  } catch (err) {
    // Token expired or interaction required — trigger one redirect
    if (!redirecting) {
      redirecting = true;
      stashDeepLink();
      if (err instanceof InteractionRequiredAuthError) {
        await msalInstance.acquireTokenRedirect({ ...loginRequest, account: accounts[0] });
      } else {
        await msalInstance.clearCache();
        await msalInstance.loginRedirect(loginRequest);
      }
    }
    throw new Error('Re-authenticating...');
  }
}

/**
 * Re-auth after the server rejected a token the cache still believed in.
 *
 * The caller keeps the `!redirecting` test in its own condition, deliberately: when a redirect is
 * already in flight the 401 must fall through to the ordinary `ApiError` path rather than throwing
 * the re-auth sentinel, which is the pre-existing behaviour.
 */
async function reauthenticate(): Promise<void> {
  redirecting = true;
  stashDeepLink();
  const { msalInstance } = await import('../main');
  await msalInstance.clearCache();
  await msalInstance.loginRedirect(loginRequest);
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  // Skip Content-Type when the caller is uploading a FormData body --
  // the browser must set `multipart/form-data; boundary=...` itself,
  // and forcing JSON breaks the parser server-side. JSON-bodied callers
  // (the default) still get the application/json header.
  const isFormData = typeof FormData !== 'undefined'
    && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...((options.headers as Record<string, string>) || {}),
  };

  // "View as user" — backend resolves to the target identity for reads;
  // any write request is rejected by middleware so the admin can't
  // accidentally take actions attributed to the impersonated user.
  const impersonate = getImpersonationEmail();
  if (impersonate) {
    headers['X-Impersonate-Email'] = impersonate;
  }

  if (isEntraConfigured) {
    await attachAuth(headers);
  } else {
    headers['X-Api-Key'] = API_KEY;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  // If backend returns 401, force re-auth
  if (response.status === 401 && isEntraConfigured && !redirecting) {
    await reauthenticate();
    throw new Error('Session expired, re-authenticating...');
  }

  if (!response.ok) {
    // Parse a JSON error body if possible so callers can branch on `detail`.
    let detail: unknown = null;
    try {
      detail = await response.clone().json();
    } catch {
      try {
        detail = await response.text();
      } catch {
        // ignore
      }
    }
    throw new ApiError(
      `API error: ${response.status} ${response.statusText}`,
      response.status,
      detail,
    );
  }
  // 204 No Content or otherwise empty body -- skip the JSON parse so DELETE
  // and similar endpoints don't blow up with "Unexpected end of JSON input."
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text);
}

/**
 * Variant that returns raw text rather than JSON. Used by endpoints
 * that respond with text/plain (e.g. the prompt-preview admin
 * routes). All other behavior — Entra auth header, impersonation
 * header, 401 redirect, error parsing — matches apiFetch.
 */
export async function apiFetchText(
  path: string,
  options: RequestInit = {},
): Promise<string> {
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };
  const impersonate = getImpersonationEmail();
  if (impersonate) {
    headers['X-Impersonate-Email'] = impersonate;
  }
  if (isEntraConfigured) {
    await attachAuth(headers);
  } else {
    headers['X-Api-Key'] = API_KEY;
  }

  const response = await fetch(`${BASE_URL}${path}`, { ...options, headers });
  if (response.status === 401 && isEntraConfigured && !redirecting) {
    await reauthenticate();
    throw new Error('Session expired, re-authenticating...');
  }
  if (!response.ok) {
    let detail: unknown = null;
    try {
      detail = await response.clone().text();
    } catch {
      // ignore
    }
    throw new ApiError(
      `API error: ${response.status} ${response.statusText}`,
      response.status,
      detail,
    );
  }
  return await response.text();
}


/**
 * Variant that returns the raw response Blob — for binary downloads
 * (e.g. NDA artifact DOCX files). All other behavior — Entra auth
 * header, impersonation header, 401 redirect, error parsing — matches
 * apiFetch.
 */
export async function apiFetchBlob(
  path: string,
  options: RequestInit = {},
): Promise<Blob> {
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };
  const impersonate = getImpersonationEmail();
  if (impersonate) {
    headers['X-Impersonate-Email'] = impersonate;
  }
  if (isEntraConfigured) {
    await attachAuth(headers);
  } else {
    headers['X-Api-Key'] = API_KEY;
  }

  const response = await fetch(`${BASE_URL}${path}`, { ...options, headers });
  if (response.status === 401 && isEntraConfigured && !redirecting) {
    await reauthenticate();
    throw new Error('Session expired, re-authenticating...');
  }
  if (!response.ok) {
    let detail: unknown = null;
    try {
      detail = await response.clone().json();
    } catch {
      try {
        detail = await response.text();
      } catch {
        // ignore
      }
    }
    throw new ApiError(
      `API error: ${response.status} ${response.statusText}`,
      response.status,
      detail,
    );
  }
  return await response.blob();
}


export class ApiError extends Error {
  status: number;
  detail: unknown;

  constructor(message: string, status: number, detail: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Human-readable message for a caught API failure. FastAPI's HTTPException
 * bodies look like {"detail": "<string>"}; unwrap that so the operator sees
 * the server's reason ("Render failed in step 1 body: 'FirstNamesAlpha' is
 * undefined") instead of the bare "API error: 400 Bad Request". Non-string
 * details (Pydantic 422 arrays) fall back to the status message.
 */
export function extractApiMessage(e: unknown, fallback = 'unknown error'): string {
  if (e instanceof ApiError) {
    const d = e.detail as unknown;
    if (typeof d === 'string' && d.trim()) return d;
    if (d && typeof d === 'object' && 'detail' in d) {
      const inner = (d as { detail: unknown }).detail;
      if (typeof inner === 'string' && inner.trim()) return inner;
    }
    return e.message;
  }
  return e instanceof Error ? e.message : fallback;
}
